import React, { useState, useEffect } from 'react';

interface FilterOption {
  id: string;
  label: string;
}

interface FilterOptionsProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilter: (selectedFilters: Record<string, string[]>) => void;
  filterCategories: {
    [key: string]: FilterOption[];
  };
  initialFilters?: Record<string, string[]>;
}

const FilterOptions: React.FC<FilterOptionsProps> = ({
  isOpen,
  onClose,
  onApplyFilter,
  filterCategories,
  initialFilters = {}
}) => {
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(initialFilters);

  useEffect(() => {
    if (isOpen) {
      setSelectedFilters(initialFilters);
    }
  }, [isOpen, initialFilters]);

  const handleFilterChange = (category: string, optionId: string, checked: boolean) => {
    setSelectedFilters(prev => {
      const categoryFilters = [...(prev[category] || [])];
      
      if (checked) {
        if (!categoryFilters.includes(optionId)) {
          categoryFilters.push(optionId);
        }
      } else {
        const index = categoryFilters.indexOf(optionId);
        if (index !== -1) {
          categoryFilters.splice(index, 1);
        }
      }
      
      return {
        ...prev,
        [category]: categoryFilters
      };
    });
  };

  const handleApplyFilter = () => {
    onApplyFilter(selectedFilters);
    onClose();
  };

  const handleClearFilters = () => {
    setSelectedFilters({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] flex flex-col shadow-md">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-blue-900">Opções de Filtro</h3>
          <button 
            className="flex items-center justify-center bg-transparent border-none cursor-pointer text-lg text-gray-600 w-8 h-8" 
            onClick={onClose}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-grow">
          {Object.entries(filterCategories).map(([category, options]) => (
            <div key={category} className="mb-5">
              <h4 className="text-md font-bold">{category}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {options.map(option => (
                  <div key={option.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`filter-${category}-${option.id}`}
                      checked={selectedFilters[category]?.includes(option.id) || false}
                      onChange={(e) => handleFilterChange(category, option.id, e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label 
                      htmlFor={`filter-${category}-${option.id}`} 
                      className="text-sm text-gray-700"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button 
            className="flex items-center justify-center px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors" 
            onClick={handleClearFilters}
          >
            Limpar Filtros
          </button>
          <button 
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors" 
            onClick={handleApplyFilter}
          >
            Aplicar Filtros
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterOptions;
